import React, { useState, useEffect } from 'react';
import { useGridApiRef, GridRowModes } from '@mui/x-data-grid-premium';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { DataGridIdentifiers, DatagridPremiumUi } from '../Interface';
import { dataGridPremiumColumns } from '../../utils/helper';
import appService from '../../service/app.service';
import useUsers from '../../context/UsersContext';
import useParam from '../../context/ParamContext';
import DrillArrow from '../Interface/DrillArrow';

const EventLogger = () => {
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [refetchTable, setRefetchTable] = useState(false);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(params).length) getParam();

    const intervalId = setInterval(() => {
      setRefetchTable(true);
    }, 300000);

    return () => clearInterval(intervalId);
  }, []);

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'createdAt', sort: 'desc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 100,
        page: 0,
      },
    },
    density: 'compact',
  };

  const columns = () => {
    return dataGridPremiumColumns([
      {
        field: 'createdAt',
        headerName: 'Dátum/Idő',
        type: 'dateTime',
        sortable: true,
        valueGetter: (params) => {
          return new Date(params);
        },
        renderCell: (params) => {
          const date = new Date(params.value);
          return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
        },
      },

      { field: 'operation', headerName: 'Művelet', type: 'string' },
      { field: 'description', headerName: 'Leírás', type: 'string' },
      {
        field: 'comment',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: JSON.parse(params?.EVENTLOG_ADMINS ?? '[]').includes(user?.userId),
      },
      {
        field: 'user',
        headerName: 'Műveletet végezte',
        type: 'string',
        renderCell: (params) => {
          return params?.row?.user?.userName;
        },
      },
      {
        field: 'navigate',
        headerName: '',
        renderCell: (params) => {
          return (
            <>
              {params?.row.navigate ? (
                <DrillArrow
                  onClick={(e) => {
                    navigate(`/app/${params.row.navigate}`);
                  }}
                />
              ) : null}
            </>
          );
        },
      },
    ]);
  };

  const getList = (gridState) => {
    appService.getEventLog({ ...gridState, userId: user.userId }).then((data) => {
      setReports(data);
    });
  };

  const processRowUpdate = (newRow, oldRow) => {
    if (newRow.comment !== oldRow.comment) {
      appService
        .updateEventLog(newRow)
        .then(() => {
          return newRow;
        })
        .catch(() => {
          apiRef.current.updateRows([{ id: newRow.id, row: oldRow, mode: GridRowModes.Edit }]);
        });
    } else return oldRow;
  };

  return (
    <div>
      <DatagridPremiumUi
        apiRef={apiRef}
        datagridInitialState={initialState}
        datagridIdentifier={DataGridIdentifiers.eventTable}
        dataLoader={getList}
        height={580}
        columns={columns()}
        rows={reports.rows}
        rowCount={reports.rowCount}
        refetch={{ refetchTable, setRefetchTable }}
        paginationAndfilterMode="server"
        editMode="row"
        onCellDoubleClick={(_params, event) => {
          event.defaultMuiPrevented = true;
        }}
        onCellClick={(params, event) => {
          const rowMode = apiRef.current.getRowMode(params.row.id);
          if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
        }}
        disableRowSelectionOnClick
        headerFilters={false}
        getRowHeight={() => 'auto'}
        processRowUpdate={processRowUpdate}
      />
    </div>
  );
};

export default EventLogger;
